import { useState, useMemo } from 'react';
import { UserPlus, Shield, User, Mail, Clock, RefreshCw, X, Copy, Check } from 'lucide-react';
import { Button, Card, Badge, Select } from '@/components/ui';
import { useSettingsStore, useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { InvitationForm } from './InvitationForm';
import type { OrganizationMember, Invitation, UserRole } from '@perfin/shared';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'standard', label: 'Standard' },
];

const ROLE_DISPLAY: Record<UserRole, { icon: typeof Shield; label: string; color: string }> = {
  superAdmin: { icon: Shield, label: 'Super Admin', color: 'text-primary-500' },
  admin: { icon: Shield, label: 'Admin', color: 'text-primary-500' },
  standard: { icon: User, label: 'Standard', color: 'text-gray-400' },
};

// Sort order: standard users first (A-Z), then admins (A-Z), then superAdmin last
const ROLE_SORT_ORDER: Record<UserRole, number> = {
  standard: 0,
  admin: 1,
  superAdmin: 2,
};

export function UserSection() {
  const { user } = useAuthStore();
  const { members, invitations, currentUserRole, changeUserRole, removeMember, cancelInvitation, resendInvite } = useSettingsStore();
  const isSuperAdmin = currentUserRole === 'superAdmin';
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Sort members: standard (A-Z), admin (A-Z), superAdmin (last)
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const roleOrderA = ROLE_SORT_ORDER[a.role];
      const roleOrderB = ROLE_SORT_ORDER[b.role];
      if (roleOrderA !== roleOrderB) {
        return roleOrderA - roleOrderB;
      }
      // Same role - sort alphabetically by name
      const nameA = (a.displayName || a.email || '').toLowerCase();
      const nameB = (b.displayName || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [members]);

  const handleRoleChange = async (member: OrganizationMember, newRole: UserRole) => {
    if (member.userId === user?.uid) {
      toast.error("You can't change your own role");
      return;
    }

    try {
      await changeUserRole(member.userId, newRole);
      toast.success(`${member.displayName || member.email}'s role updated to ${newRole}`);
    } catch {
      toast.error('Failed to change user role');
    }
  };

  const handleRemoveMember = (member: OrganizationMember) => {
    if (member.userId === user?.uid) {
      toast.error("You can't remove yourself");
      return;
    }

    confirmDelete({
      title: 'Remove Member',
      message: `Are you sure you want to remove ${member.displayName || member.email}?\n\nThey will become the owner of their own organization. All items they own will transfer with them. Items in their collection owned by others will return to those owners.`,
      onConfirm: async () => {
        try {
          await removeMember(member.userId);
          toast.success('Member removed and transferred to their own organization');
        } catch {
          toast.error('Failed to remove member');
        }
      },
    });
  };

  const handleCancelInvitation = (invite: Invitation) => {
    confirmDelete({
      title: 'Cancel Invitation',
      message: `Are you sure you want to cancel the invitation to ${invite.email}?`,
      onConfirm: async () => {
        try {
          await cancelInvitation(invite.id);
          toast.success('Invitation cancelled');
        } catch {
          toast.error('Failed to cancel invitation');
        }
      },
    });
  };

  const handleRenewInvitation = async (invite: Invitation) => {
    setResending(invite.id);
    try {
      await resendInvite(invite.id);
      toast.success(`Invitation renewed for ${invite.email} - expires in 7 days`);
    } catch {
      toast.error('Failed to renew invitation');
    } finally {
      setResending(null);
    }
  };

  const handleCopyInviteLink = async (invite: Invitation) => {
    // Token is not stored (only tokenHash for security), so we must resend to get a new token
    setResending(invite.id);
    try {
      const newToken = await resendInvite(invite.id);
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/invite/${newToken}`;
      await navigator.clipboard.writeText(link);
      setCopiedId(invite.id);
      toast.success('Link copied (invitation renewed)');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy link');
    } finally {
      setResending(null);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'Unknown';
    return date.toDate?.()?.toLocaleDateString() || 'Unknown';
  };

  const getExpiryStatus = (expiresAt: any) => {
    if (!expiresAt) return { text: 'Unknown', isExpired: false };
    const expiry = expiresAt.toDate?.();
    if (!expiry) return { text: 'Unknown', isExpired: false };

    const now = new Date();
    const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return { text: 'Expired', isExpired: true };
    } else if (daysLeft === 0) {
      return { text: 'Expires today', isExpired: false };
    } else if (daysLeft === 1) {
      return { text: 'Expires tomorrow', isExpired: false };
    } else {
      return { text: `Expires in ${daysLeft} days`, isExpired: false };
    }
  };

  return (
    <div className="space-y-8">
      {/* Members Section */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Members</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage who has access to your organization
            </p>
          </div>
          <Button onClick={() => setShowInviteForm(true)} leftIcon={<UserPlus className="h-4 w-4" />}>
            Invite Member
          </Button>
        </div>

        <Card padding="none">
          <div className="divide-y divide-gray-200">
            {sortedMembers.map((member) => {
              const isCurrentUser = member.userId === user?.uid;
              const canRemove = isSuperAdmin && !isCurrentUser && member.role !== 'superAdmin';

              return (
                <div key={member.userId} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-10">
                    {member.photoURL ? (
                      <img
                        src={member.photoURL}
                        alt={member.displayName || ''}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-medium">
                        {(member.displayName || member.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {member.displayName || member.email}
                      </span>
                      {isCurrentUser && (
                        <Badge variant="secondary" size="sm">You</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>

                  {/* Role - fixed width for alignment */}
                  <div className="flex items-center gap-2 w-40">
                    {(() => {
                      const roleInfo = ROLE_DISPLAY[member.role];
                      const RoleIcon = roleInfo.icon;
                      return <RoleIcon className={`h-4 w-4 flex-shrink-0 ${roleInfo.color}`} />;
                    })()}

                    {/* Only superAdmin can change roles, and cannot change their own or another superAdmin */}
                    {isSuperAdmin && !isCurrentUser && member.role !== 'superAdmin' ? (
                      <div className="w-28">
                        <Select
                          value={member.role}
                          onChange={(e) => handleRoleChange(member, e.target.value as UserRole)}
                          options={ROLE_OPTIONS}
                          className="text-sm"
                        />
                      </div>
                    ) : (
                      <span className="text-sm font-medium text-gray-700 w-28">
                        {ROLE_DISPLAY[member.role].label}
                      </span>
                    )}
                  </div>

                  {/* Joined date - fixed width for alignment */}
                  <div className="text-sm text-gray-400 w-36">
                    Joined {formatDate(member.joinedAt)}
                  </div>

                  {/* Remove button - fixed width column, always present for alignment */}
                  <div className="w-20">
                    {canRemove && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Pending Invitations Section */}
      {invitations.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Invitations</h2>
            <p className="text-sm text-gray-500 mt-1">
              Pending and expired invitations - click Renew to extend expiry
            </p>
          </div>

          <Card padding="none">
            <div className="divide-y divide-gray-200">
              {invitations.map((invite) => {
                const expiry = getExpiryStatus(invite.expiresAt);

                return (
                  <div key={invite.id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                    {/* Icon */}
                    <div className="flex-shrink-0 p-2 bg-gray-100 rounded-full">
                      <Mail className="h-5 w-5 text-gray-500" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{invite.email}</span>
                        <Badge
                          variant={invite.role === 'admin' ? 'primary' : 'secondary'}
                          size="sm"
                        >
                          {invite.role}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">
                        Invited by {invite.inviterName || 'Unknown'}
                      </p>
                    </div>

                    {/* Expiry */}
                    <div className="flex items-center gap-1 text-sm">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className={expiry.isExpired ? 'text-red-500' : 'text-gray-500'}>
                        {expiry.text}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRenewInvitation(invite)}
                        disabled={resending === invite.id}
                        className="flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50"
                      >
                        <RefreshCw className={`h-3 w-3 ${resending === invite.id ? 'animate-spin' : ''}`} />
                        Renew
                      </button>
                      <button
                        onClick={() => handleCancelInvitation(invite)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Cancel invitation"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleCopyInviteLink(invite)}
                        className={`flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors ${
                          copiedId === invite.id
                            ? 'text-green-600 bg-green-50'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Copy invite link"
                      >
                        {copiedId === invite.id ? (
                          <>
                            <Check className="h-3 w-3" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <InvitationForm onClose={() => setShowInviteForm(false)} />
      )}
    </div>
  );
}
